<template>
  <div class="student-analytics">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon students">
              <a-icon type="team" />
            </div>
            <div class="stat-content">
              <div class="stat-value">
                {{ (data && data.summary && data.summary.totalStudents) || 0 }}
              </div>
              <div class="stat-label">
                {{ $t("financialStats.totalStudents") }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon revenue">
              <a-icon type="dollar" />
            </div>
            <div class="stat-content">
              <div class="stat-value">
                {{
                  formatCurrency(
                    data && data.summary && data.summary.totalRevenue
                  )
                }}
              </div>
              <div class="stat-label">
                {{ $t("financialStats.totalRevenue") }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon average">
              <a-icon type="calculator" />
            </div>
            <div class="stat-content">
              <div class="stat-value">
                {{
                  formatCurrency(
                    data &&
                      data.summary &&
                      data.summary.averagePaymentPerStudent
                  )
                }}
              </div>
              <div class="stat-label">
                {{ $t("financialStats.averagePaymentPerStudent") }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Paying Students -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  {{ $t("financialStats.topPayingStudents") }}
                </h6>
                <a-button
                  type="link"
                  @click="showAllStudents = !showAllStudents"
                >
                  {{
                    showAllStudents
                      ? $t("financialStats.showLess")
                      : $t("financialStats.showAll")
                  }}
                </a-button>
              </div>
            </div>
            <div class="card-body">
              <a-table
                :columns="studentColumns"
                :dataSource="displayedStudents"
                :pagination="false"
                size="small"
                :scroll="{ x: 800 }"
              >
                <template slot="rank" slot-scope="text, record, index">
                  <div class="rank-badge" :class="getRankClass(index)">
                    {{ index + 1 }}
                  </div>
                </template>
                <template slot="studentName" slot-scope="text, record">
                  <div class="student-info">
                    <a-avatar
                      size="large"
                      v-if="record.studentPhoto"
                      :src="
                        settings.resourcesUrl.replace(
                          'FILE_PATH',
                          record.studentPhoto
                        )
                      "
                    />
                    <a-avatar
                      size="large"
                      style="color: #f56a00; background-color: #fde3cf"
                      v-else
                    >
                      {{ text[0].toUpperCase() }}
                    </a-avatar>
                    <div class="student-details">
                      <div class="student-name">{{ text }}</div>
                    </div>
                  </div>
                </template>
                <template slot="totalPaid" slot-scope="text">
                  <span class="amount-value">{{ formatCurrency(text) }}</span>
                </template>
                <template slot="invoiceCount" slot-scope="text">
                  <a-badge
                    :count="text"
                    :number-style="{ backgroundColor: '#1890ff' }"
                  />
                </template>
                <template slot="paymentMethods" slot-scope="text">
                  <div class="payment-methods">
                    <a-tag
                      v-for="(method, index) in text"
                      :key="`${method}-${index}`"
                      :color="getMethodColor(method)"
                      size="small"
                    >
                      {{ getMethodName(method) }}
                    </a-tag>
                  </div>
                </template>
                <template slot="lastPaymentDate" slot-scope="text">
                  <span class="date-text">{{ formatDate(text) }}</span>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Methods Distribution -->
      <div class="row">
        <div class="col-lg-6 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                {{ $t("financialStats.paymentMethodsDistribution") }}
              </h6>
            </div>
            <div class="card-body">
              <apexchart
                :options="paymentMethodsChartOptions"
                :series="paymentMethodsChartData.series"
                type="donut"
                height="300"
                class="chart-container"
              />
            </div>
          </div>
        </div>

        <div class="col-lg-6 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t("financialStats.paymentFrequency") }}</h6>
            </div>
            <div class="card-body">
              <div class="frequency-stats">
                <div class="frequency-item">
                  <div class="frequency-label">
                    {{ $t("financialStats.singlePayment") }}
                  </div>
                  <div class="frequency-value">{{ singlePaymentCount }}</div>
                  <div class="frequency-bar">
                    <div
                      class="frequency-fill"
                      :style="{ width: singlePaymentPercent + '%' }"
                    ></div>
                  </div>
                </div>
                <div class="frequency-item">
                  <div class="frequency-label">
                    {{ $t("financialStats.multiplePayments") }}
                  </div>
                  <div class="frequency-value">{{ multiplePaymentCount }}</div>
                  <div class="frequency-bar">
                    <div
                      class="frequency-fill"
                      :style="{ width: multiplePaymentPercent + '%' }"
                    ></div>
                  </div>
                </div>
                <div class="frequency-item">
                  <div class="frequency-label">
                    {{ $t("financialStats.frequentPayers") }}
                  </div>
                  <div class="frequency-value">{{ frequentPayerCount }}</div>
                  <div class="frequency-bar">
                    <div
                      class="frequency-fill"
                      :style="{ width: frequentPayerPercent + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "StudentAnalytics",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        summary: {
          totalStudents: 0,
          totalRevenue: 0,
          averagePaymentPerStudent: 0,
        },
        students: [],
      }),
    },
  },
  data() {
    return {
      showAllStudents: false,
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  computed: {
    ...mapState(["settings"]),

    studentColumns() {
      return [
        {
          title: this.$t("financialStats.rank"),
          dataIndex: "rank",
          key: "rank",
          width: 60,
          scopedSlots: { customRender: "rank" },
        },
        {
          title: this.$t("financialStats.student"),
          dataIndex: "studentName",
          key: "studentName",
          scopedSlots: { customRender: "studentName" },
        },
        {
          title: this.$t("financialStats.totalPaid"),
          dataIndex: "totalPaid",
          key: "totalPaid",
          scopedSlots: { customRender: "totalPaid" },
        },
        {
          title: this.$t("financialStats.invoices"),
          dataIndex: "invoiceCount",
          key: "invoiceCount",
          scopedSlots: { customRender: "invoiceCount" },
        },
        {
          title: this.$t("financialStats.paymentMethods"),
          dataIndex: "paymentMethods",
          key: "paymentMethods",
          scopedSlots: { customRender: "paymentMethods" },
        },
        {
          title: this.$t("financialStats.lastPayment"),
          dataIndex: "lastPaymentDate",
          key: "lastPaymentDate",
          scopedSlots: { customRender: "lastPaymentDate" },
        },
      ];
    },

    displayedStudents() {
      const students = (this.data && this.data.students) || [];
      if (this.showAllStudents) {
        return students;
      }
      return students.slice(0, 10);
    },

    paymentMethodsChartData() {
      const methodCounts = {};
      const students = (this.data && this.data.students) || [];
      students.forEach((student) => {
        if (student.paymentMethods) {
          student.paymentMethods.forEach((method) => {
            methodCounts[method] = (methodCounts[method] || 0) + 1;
          });
        }
      });

      const total = Object.values(methodCounts).reduce(
        (sum, count) => sum + count,
        0
      );
      if (total === 0) {
        return {
          series: [],
        };
      }

      const methods = Object.entries(methodCounts)
        .map(([method, count]) => ({
          method,
          count,
          percentage: ((count / total) * 100).toFixed(1),
        }))
        .sort((a, b) => b.count - a.count);

      return {
        series: methods.map((m) => m.count),
        labels: methods.map((m) => this.getMethodName(m.method)),
      };
    },

    singlePaymentCount() {
      const students = (this.data && this.data.students) || [];
      return students.filter((s) => s.invoiceCount === 1).length;
    },

    multiplePaymentCount() {
      const students = (this.data && this.data.students) || [];
      return students.filter((s) => s.invoiceCount > 1 && s.invoiceCount <= 5)
        .length;
    },

    frequentPayerCount() {
      const students = (this.data && this.data.students) || [];
      return students.filter((s) => s.invoiceCount > 5).length;
    },

    singlePaymentPercent() {
      const students = (this.data && this.data.students) || [];
      const total = students.length;
      return total > 0
        ? ((this.singlePaymentCount / total) * 100).toFixed(1)
        : 0;
    },

    multiplePaymentPercent() {
      const students = (this.data && this.data.students) || [];
      const total = students.length;
      return total > 0
        ? ((this.multiplePaymentCount / total) * 100).toFixed(1)
        : 0;
    },

    frequentPayerPercent() {
      const students = (this.data && this.data.students) || [];
      const total = students.length;
      return total > 0
        ? ((this.frequentPayerCount / total) * 100).toFixed(1)
        : 0;
    },

    paymentMethodsChartOptions() {
      return {
        chart: {
          type: "donut",
          height: 300,
        },
        colors: ["#1890ff", "#52c41a", "#faad14", "#f5222d", "#722ed1"],
        labels: this.paymentMethodsChartData.labels,
        dataLabels: {
          enabled: true,
          formatter: (val) => {
            return Number(val).toFixed(1) + "%";
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "60%",
            },
          },
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return value + " students";
            },
          },
        },
        legend: {
          position: "bottom",
        },
      };
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) {
        return `0 ${this.currency}`;
      }
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + `M ${this.currency}`;
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + `K ${this.currency}`;
      }
      return value.toLocaleString() + ` ${this.currency}`;
    },

    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    getStudentColor(studentId) {
      const colors = ["#1890ff", "#52c41a", "#faad14", "#ff4d4f", "#722ed1"];
      const hash = studentId.split("").reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0);
      return colors[Math.abs(hash) % colors.length];
    },

    getMethodName(method) {
      return this.$t("recette.paymentMethod." + method);
    },

    getMethodColor(method) {
      const colors = {
        ESPESE: "green",
        CHEQUE: "blue",
        TPE: "orange",
        TRAITE: "purple",
        VIREMENT: "red",
      };
      return colors[method] || "default";
    },

    getRankClass(index) {
      if (index === 0) return "rank-gold";
      if (index === 1) return "rank-silver";
      if (index === 2) return "rank-bronze";
      return "rank-default";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/shared.scss";

.student-analytics {
  .amount-value {
    font-weight: 600;
    color: #52c41a;
  }

  .date-text {
    font-size: 12px;
    color: #8c8c8c;
  }

  .payment-methods {
    .ant-tag {
      margin: 2px;
    }
  }

  .frequency-stats {
    .frequency-item {
      margin-bottom: 20px;

      .frequency-label {
        font-size: 14px;
        color: #595959;
        margin-bottom: 8px;
      }

      .frequency-value {
        font-size: 18px;
        font-weight: 600;
        color: #262626;
        margin-bottom: 8px;
      }

      .frequency-bar {
        height: 8px;
        background: #f0f0f0;
        border-radius: 4px;
        overflow: hidden;

        .frequency-fill {
          height: 100%;
          background: linear-gradient(90deg, #1890ff 0%, #52c41a 100%);
          border-radius: 4px;
          transition: width 0.3s ease;
        }
      }
    }
  }
}
</style>
